import React, { type KeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { cssMap } from '@atlaskit/css';
import type { EditorActions } from '@atlaskit/editor-core';
import Link from '@atlaskit/link';
import { Box, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import {
	AddItemButton,
	checkItems,
	ConfigPanelFeedbackCollector,
	ConfigPanelGrouping,
	ExtensionsConfigWrapper,
	FeedbackType,
	FormFieldErrorMessage,
	ITEM_FIELDS,
	LayoutOptions,
	type LayoutOptionsProps,
	NoExtensionData,
	PremiumGateForExtensionConfigPanel,
	RenderVariant,
	SegmentationTargets,
	type TabProps,
	useExtensionsActions,
	useIsDisabledContext,
	useProductDependencies,
} from '@atlassian/company-hub-common';

import {
	CARDS_EXTENSION_ANALYTIC_SOURCE,
	CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
} from '../../constants';
import { useCardsExtensionParametersAndActions } from '../../controllers/use-cards-extension-parameters-and-actions';
import {
	type Card,
	type CardsExtensionParameters,
	CardSize,
	CardsLayout,
	DEFAULT_DYNAMIC_CARDS_COUNT,
} from '../../types';
import { CardsExtensionIcon } from '../assets/cards-extension-icon';
import {
	CardsFixedPreview,
	CardsFixedPreviewDark,
	CardsFlexiblePreview,
	CardsFlexiblePreviewDark,
} from '../assets/cards-preview';
import { getDefaultCards } from '../cards-manifest';

import { CardsArea } from './content-section/cards-area';
import { CardsConfigContentSectionDisplayOptions } from './content-section/display-options';
import { DynamicVariantContent } from './content-section/dynamic-variant-content';
import { CardsConfigContentSectionMain } from './content-section/main';
import { messages } from './messages';
import { CardsConfigStylesSectionGeneral } from './styles-section/general';

export const layoutOptions: {
	description: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	label: {
		defaultMessage: string;
		description: string;
		id: string;
	};
	value: CardsLayout;
}[] = [
	{
		label: messages.layoutsCardsFixedImageHeightLabel,
		description: messages.layoutsCardsFixedImageHeightDescription,
		value: CardsLayout.FIXED_IMAGE_HEIGHT,
	},
	{
		label: messages.layoutsCardsFlexibleImageHeightLabel,
		description: messages.layoutsCardsFlexibleImageHeightDescription,
		value: CardsLayout.FLEXIBLE_IMAGE_HEIGHT,
	},
];

enum TabSections {
	GENERAL = 'general',
	MAIN = 'main',
	DISPLAY_OPTIONS = 'displayOptions',
	CARDS = 'cards',
	LAYOUTS = 'layouts',
}

enum TabValues {
	STYLES = 'styles',
	CONTENT = 'content',
}

// button keyboard accessibility spec: https://www.w3.org/WAI/ARIA/apg/patterns/button/examples/button/
const TRIGGER_BUTTON_KEYS = ['Enter', 'Spacebar', ' '];
export const LINK_CARDS_LIMIT = 20;

const styles = cssMap({
	linkCardsListBuilderFooterWrapper: {
		paddingBlock: token('space.300'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.100'),
	},
	stickyFooter: {
		paddingInline: token('space.050'),
		marginInline: token('space.negative.050'),
		position: 'sticky',
		bottom: token('space.0'),
		backgroundColor: token('elevation.surface'),
	},
});

const CardsConfigPanelFeedbackCollector = () => {
	return (
		<ConfigPanelFeedbackCollector
			source={CARDS_EXTENSION_ANALYTIC_SOURCE}
			type={FeedbackType.CARDS}
		/>
	);
};

export type CardsConfigPanelContentProps = {
	closeConfigPanel: () => void;
	contentId: string;
	editorActions: EditorActions;
	extensionKey: string;
	localId: string;
};

export const CardsConfigPanelContent = ({
	contentId,
	closeConfigPanel,
	localId,
	editorActions,
	extensionKey,
}: CardsConfigPanelContentProps): React.JSX.Element => {
	const { cardsActions, cardsParameters } = useCardsExtensionParametersAndActions({
		localId,
		editorActions,
	});
	const { formatMessage } = useIntl();

	const {
		cards,
		renderVariant,
		cardCount,
		filters,
		isAvatarShown,
		isPublishDateShown,
		isSocialStatsShown,
		isIconShown,
		isLabelShown,
		segmentationTarget,
		audienceSegmentationGroupIds,
		layout,
		size,
		isDescriptionShown,
	} = cardsParameters || {};
	const { addCard, updateMainFormField, updateCardFormFieldForAllCards } = cardsActions;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { setIsInAutoFocusMode, setIsInViewMode } = useExtensionsActions();
	const [tabSelected, setTabSelected] = useState<TabValues>(TabValues.STYLES);

	const cardsListRef = useRef<HTMLDivElement>(null);
	const isDisabled = useIsDisabledContext();

	const { isCompanyHub, enableSmartCardsFields, showConfigPreambleSupportLink } =
		useProductDependencies();

	useEffect(() => {
		setIsInViewMode(false);
	}, [setIsInViewMode]);

	const handleAddCardKeyPress = (event: KeyboardEvent) => {
		if (TRIGGER_BUTTON_KEYS.includes(event.key)) {
			setTimeout(() => {
				(cardsListRef?.current?.lastChild as HTMLDivElement)?.focus();
			}, 100);
		}
	};

	const updateRenderVariant = useCallback(
		(value: RenderVariant) => {
			setIsInAutoFocusMode(false);
			const fieldsToUpdate: Partial<CardsExtensionParameters> = {
				renderVariant: value,
			};

			if (value === RenderVariant.MANUAL) {
				if (cards.length === 0) {
					fieldsToUpdate.cards = getDefaultCards();
				}
			}

			if (value === RenderVariant.DYNAMIC) {
				fieldsToUpdate.cardCount = cardCount || DEFAULT_DYNAMIC_CARDS_COUNT;
				fieldsToUpdate.filters = filters || undefined;
			}

			// Clear segmentation target if it's set to EACH and switching variant
			if (segmentationTarget === SegmentationTargets.EACH) {
				fieldsToUpdate.segmentationTarget = undefined;
				fieldsToUpdate.isSegmentationShown = false;
				updateCardFormFieldForAllCards({
					[ITEM_FIELDS.AUDIENCE_SEGMENTATION_GROUP_IDS]: undefined,
				});
			}

			updateMainFormField(fieldsToUpdate);

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'renderVariantSwitcher',
					source: CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE,
					attributes: {
						renderVariant: value,
					},
				},
			}).fire();
		},
		[
			cards,
			setIsInAutoFocusMode,
			updateMainFormField,
			updateCardFormFieldForAllCards,
			cardCount,
			filters,
			createAnalyticsEvent,
			segmentationTarget,
		],
	);

	const handleTabClick = useCallback((tab: TabProps<TabSections>) => {
		setTabSelected(tab.value as TabValues);
	}, []);

	const renderConfigPanelFooter = useCallback(() => {
		const hasReachedCardLimit = cards?.length >= LINK_CARDS_LIMIT;
		const isManualCard = renderVariant === RenderVariant.MANUAL;
		const isContentTabSelected = tabSelected === TabValues.CONTENT;

		const canShowCardsMessages = isManualCard && isContentTabSelected;
		const showAddCardsButton = !hasReachedCardLimit && canShowCardsMessages;
		const showCardsErrorMessage = !checkItems(cards) && canShowCardsMessages;

		// when Cards is disabled (ie. premium gated), don't show the footer
		if (isDisabled) {
			return null;
		}

		return (
			<Box xcss={styles.stickyFooter}>
				<Box xcss={styles.linkCardsListBuilderFooterWrapper}>
					{showAddCardsButton && (
						<AddItemButton
							addItem={addCard}
							onAddItemKeyPress={handleAddCardKeyPress}
							analyticsSource={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
							actionSubjectIdSuffix="Card"
						/>
					)}
					{showCardsErrorMessage && (
						<FormFieldErrorMessage>
							<FormattedMessage {...messages.cardsInvalidMessage} />
						</FormFieldErrorMessage>
					)}
					{isContentTabSelected ? undefined : <CardsConfigPanelFeedbackCollector />}
				</Box>
			</Box>
		);
	}, [cards, addCard, isDisabled, renderVariant, tabSelected]);

	const previewImage = useCallback<NonNullable<LayoutOptionsProps<CardsLayout>['previewImage']>>(
		(layout) => {
			if (layout === CardsLayout.FIXED_IMAGE_HEIGHT) {
				return { imageSrc: CardsFixedPreview, imageSrcDark: CardsFixedPreviewDark };
			} else {
				return { imageSrc: CardsFlexiblePreview, imageSrcDark: CardsFlexiblePreviewDark };
			}
		},
		[],
	);

	if (!cardsParameters) {
		return <NoExtensionData source={extensionKey} isConfigPanel />;
	}

	const updateCards = (cards: Card[]) => updateMainFormField({ cards });

	const tabSections: TabProps<TabSections>[] = [
		{
			label: formatMessage(messages.stylesTabLabel),
			value: TabValues.STYLES,
			tabContent: [
				{
					sectionId: TabSections.GENERAL,
					title: formatMessage(messages.styleSectionGeneralTitle),
					defaultIsExpanded: true,
					component: (
						<CardsConfigStylesSectionGeneral
							extensionParameters={cardsParameters}
							extensionActions={cardsActions}
							isCompanyHub={isCompanyHub}
							isDisabled={isDisabled}
						/>
					),
				},
				...(size !== CardSize.SMALL
					? [
							{
								sectionId: TabSections.LAYOUTS,
								title: formatMessage(messages.layoutsTitle),
								defaultIsExpanded: false,
								component: (
									<LayoutOptions<CardsLayout>
										updateLayout={cardsActions.updateLayout}
										source={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
										layoutOptions={layoutOptions}
										previewImage={previewImage}
										currentLayoutSelection={layout}
									/>
								),
							},
						]
					: []),
			],
		},
		{
			label: formatMessage(messages.contentTabLabel),
			value: TabValues.CONTENT,
			tabContent: [
				{
					sectionId: TabSections.MAIN,
					component: (
						<CardsConfigContentSectionMain
							audienceSegmentationGroupIds={audienceSegmentationGroupIds}
							renderVariant={renderVariant}
							segmentationTarget={segmentationTarget}
							cards={cards}
							extensionActions={cardsActions}
							updateRenderVariant={updateRenderVariant}
							source={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
						/>
					),
				},
				...(enableSmartCardsFields &&
				(renderVariant === RenderVariant.MANUAL ||
					(renderVariant === RenderVariant.DYNAMIC && filters))
					? [
							{
								title: formatMessage(messages.contentSectionDisplayOptionsTitle),
								sectionId: TabSections.DISPLAY_OPTIONS,
								defaultIsExpanded: false,
								component: (
									<CardsConfigContentSectionDisplayOptions
										isAvatarShown={isAvatarShown}
										isPublishDateShown={isPublishDateShown}
										isSocialStatsShown={isSocialStatsShown}
										isIconShown={isIconShown}
										isLabelShown={isLabelShown}
										layout={layout}
										size={size}
										renderVariant={renderVariant}
										updateMainFormField={updateMainFormField}
										// if isDescriptionShown is undefined, it means the cards were created from before
										// and we want to enable the toggle by default
										isDescriptionShown={isDescriptionShown !== false}
									/>
								),
							},
						]
					: []),
				{
					sectionId: TabSections.CARDS,
					defaultIsExpanded: false,
					component:
						renderVariant === RenderVariant.DYNAMIC ? (
							<Box paddingBlockStart="space.400">
								<DynamicVariantContent
									extensionParameters={cardsParameters}
									extensionActions={cardsActions}
									isDisabled={isDisabled}
								/>
							</Box>
						) : (
							<CardsArea
								items={cardsParameters.cards}
								removeItem={cardsActions.removeCard}
								updateCardFormField={cardsActions.updateCardFormField}
								updateItems={updateCards}
								contentId={contentId}
								showDescription={cardsParameters.size !== CardSize.SMALL}
								layout={cardsParameters.layout}
								size={size}
								showSegmentation={Boolean(
									cardsParameters.isSegmentationShown &&
									cardsParameters.segmentationTarget === SegmentationTargets.EACH,
								)}
								itemsListRef={cardsListRef}
								isNoImageAllowed
								shouldUseCardV2UI
								showMediaPicker
								showLabel={isLabelShown}
							/>
						),
				},
			],
		},
	];

	return (
		<ExtensionsConfigWrapper
			title={<FormattedMessage {...messages.listConfigContentTitleTextLabel} />}
			icon={<CardsExtensionIcon />}
			footerComponent={renderConfigPanelFooter}
			closeConfigPanel={closeConfigPanel}
			shouldUseCardV2Styles
		>
			<Text>
				<FormattedMessage
					{...(showConfigPreambleSupportLink
						? messages.preamble
						: messages.preambleWithoutSupportLink)}
					values={{
						link: (
							<Link
								href="https://support.atlassian.com/confluence-cloud/docs/display-beautiful-dynamic-content-on-your-page-with-cards/"
								target="_blank"
							>
								<FormattedMessage {...messages.preambleSupportLinkLabel} />
							</Link>
						),
					}}
				/>
			</Text>
			<PremiumGateForExtensionConfigPanel feature="premiumCardsConfig">
				<ConfigPanelGrouping<TabSections>
					source={CARDS_EXTENSION_CONFIG_ANALYTIC_SOURCE}
					tabSections={tabSections}
					onTabClick={handleTabClick}
				/>
			</PremiumGateForExtensionConfigPanel>
		</ExtensionsConfigWrapper>
	);
};
